import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { supabaseQueryWithRetry, supabaseRpcWithRetry } from '@/lib/networkRetry';
import { QUERY_CONFIG } from '@/config/constants';
import { QUERY_KEYS } from '@/config/queries';
import { createLogger } from '@/lib/structuredLogger';

export type WearableProviderStatus = 'fitbit' | 'wearable';

export interface WearableConnectionStatus {
  connected: boolean;
  provider?: WearableProviderStatus | null;
  wearableProvider?: string | null;
}

interface UseWearableConnectionStatusOptions {
  userId?: string;
  enabled?: boolean;
}

const logger = createLogger('useWearableConnectionStatus');

export const useWearableConnectionStatus = ({
  userId,
  enabled = true,
}: UseWearableConnectionStatusOptions) => {
  return useQuery<WearableConnectionStatus>({
    queryKey: QUERY_KEYS.FITBIT_STATUS_USER(userId ?? 'anonymous'),
    queryFn: async () => {
      logger.debug('Checking connection status');
      if (!userId) {
        return { connected: false, provider: null, wearableProvider: null };
      }

      const { data, error } = await supabaseRpcWithRetry<{
        connected?: boolean;
        needs_relogin?: boolean;
      }>(supabase, 'get_fitbit_connection_status', { p_user_id: userId });

      if (error && error.code !== 'PGRST116') {
        logger.error(
          'Error checking status',
          error instanceof Error ? error : new Error(String(error)),
          {
            userId,
          },
        );
        throw error;
      }

      let connected = false;
      let provider: WearableProviderStatus | null = null;
      let wearableProvider: string | null = null;

      if (data?.connected) {
        // Connected means token set is present and refreshable.
        // Expired access tokens are still valid connections because backend refreshes automatically.
        connected = !data.needs_relogin;
        provider = connected ? 'fitbit' : null;
      }

      if (!connected) {
        const { data: wearableConnections, error: wearableError } = await supabaseQueryWithRetry(
          () =>
            supabase
              .from('wearable_connections')
              .select('provider, status')
              .eq('user_id', userId)
              .eq('status', 'active')
              .limit(1),
        );

        if (wearableError && wearableError.code !== 'PGRST116') {
          logger.warn('Error checking wearable connections', wearableError);
        }

        if (wearableConnections && wearableConnections.length > 0) {
          connected = true;
          provider = 'wearable';
          wearableProvider = wearableConnections[0]?.provider ?? null;
        }
      }

      return { connected, provider, wearableProvider };
    },
    enabled: enabled && !!userId,
    staleTime: QUERY_CONFIG.STALE_TIME.LONG,
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
